import { Typography } from '@mui/material';
import { Export, X } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, Loader, RemoveButton, Spacer } from 'theSource/components';
import { Card } from 'theSource/features/IdCards/types';
import { GET_ID_CARDS_QUERY_KEY } from 'theSource/features/IdCards/utils';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import {
  getBase64ImageSrc,
  useApiMutation,
  useWebShare,
} from 'theSource/utils';
import { IdCardHeaderStyled, IdCardStyled, ImageStyled } from '../IdCard';
import {
  ActionsWrapperStyled,
  IdCardFocusedStyled,
  IdCardFocusedWrapperStyled,
  HiddenInLandscapeWrapper,
  ModalStyled,
  PdfContentStyled,
  LoaderWrapper,
} from './IdCardFocusedStyled';
import { IdCardDeleteRequestDto } from './types';
import {
  DELETE_ID_CARD_QUERY_KEY,
  mapIdCardDeleteDto,
  generatePdfFiles,
  FILE_NAME,
} from './utils';
import { selectHomeEnabled } from 'theSource/features/Login';

interface IdCardFocusedProps {
  card: Card;
  initialFlipped: boolean;
  isFocused: boolean;
  onFocusOut: () => void;
}

export const IdCardFocused = ({
  card,
  initialFlipped,
  isFocused,
  onFocusOut,
}: IdCardFocusedProps) => {
  const { t } = useTranslation();
  const homeEnabled = useSelector(selectHomeEnabled);

  const { isLoading: isDeleting, mutate: removeCard } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    IdCardDeleteRequestDto
  >({
    queryKey: DELETE_ID_CARD_QUERY_KEY,
    queryKeysToInvalidate: GET_ID_CARDS_QUERY_KEY,
    method: 'delete',
    endpoint: '/ecards',
    onSuccess: () => onFocusOut(),
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const [isFlipped, setIsFlipped] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isPreparingFiles, setIsPreparingFiles] = useState(true);
  const [pdfContentNode, setPdfContentNode] = useState<HTMLElement | null>(
    null
  );

  const { share, canShare } = useWebShare({
    files,
    title: FILE_NAME,
  });

  const handleFlip = () => setIsFlipped(!isFlipped);
  const handleRemove = () => removeCard(mapIdCardDeleteDto(card));
  const handlePdfContentRef = (node: HTMLElement) => setPdfContentNode(node);

  useEffect(() => {
    if (pdfContentNode !== null) {
      generatePdfFiles({
        pdfElement: pdfContentNode,
        fileName: FILE_NAME,
      }).then(({ files }) => {
        setFiles(files);
        setIsPreparingFiles(false);
      });
    }
  }, [pdfContentNode]);

  useEffect(() => {
    setIsFlipped(initialFlipped);
  }, [initialFlipped]);

  const handleClose = () => {
    onFocusOut();
  };

  // Display loader until files for sharing are prepared
  const isLoading = canShare === null || files.length === 0 || isPreparingFiles;

  return (
    <ModalStyled
      open={isFocused}
      onClose={handleClose}
      data-testid="id-card-focused"
    >
      <IdCardFocusedWrapperStyled>
        <PdfContentStyled aria-hidden="true" ref={handlePdfContentRef}>
          <img
            src={getBase64ImageSrc(card.frontImage)}
            alt={t('pages.idCards:frontImageAltLabel', { name: card.name })}
          />
          {card.backImage && (
            <img
              src={getBase64ImageSrc(card.backImage)}
              alt={t('pages.idCards:backImageAltLabel', { name: card.name })}
            />
          )}
        </PdfContentStyled>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <IdCardFocusedStyled
            data-testid="id-card-focused-content"
            homeEnabled={homeEnabled}
          >
            <ActionsWrapperStyled>
              <Button
                data-testid="close-button"
                variant="tertiary"
                label={t('common:close')}
                onClick={handleClose}
                startIcon={<X size={16} weight="bold" />}
              />
              {canShare && (
                <Button
                  variant="tertiary"
                  label={t('common:share')}
                  onClick={share}
                  endIcon={<Export size={16} weight="bold" />}
                />
              )}
            </ActionsWrapperStyled>
            <IdCardHeaderStyled>
              <Typography variant="subtitle1">{card.name}</Typography>
              <Button
                data-testid="flip-button"
                variant="tertiary"
                disabled={!card.backImage}
                label={t('pages.idCards:flip')}
                onClick={handleFlip}
              />
            </IdCardHeaderStyled>
            <IdCardStyled>
              <ImageStyled
                src={getBase64ImageSrc(
                  isFlipped ? card.backImage : card.frontImage
                )}
                alt={card.name}
              />
            </IdCardStyled>
            <HiddenInLandscapeWrapper variant="body2" align="center">
              <Spacer height={9} />
              {t('pages.idCards:turnToViewInLandscapeMode')}
              <Spacer height={12} />
            </HiddenInLandscapeWrapper>
            <ActionsWrapperStyled>
              <RemoveButton
                itemName={t('pages.idCards:itemName')}
                description={t('messages.idCards:removeConfirmation')}
                onConfirm={handleRemove}
                isLoading={isDeleting}
              />
            </ActionsWrapperStyled>
          </IdCardFocusedStyled>
        )}
      </IdCardFocusedWrapperStyled>
    </ModalStyled>
  );
};
